// modules/Shop/ShopDisplay.js
// 商店商品显示模块

import UIStyleHelper from '../UIStyleHelper.js';

export default class ShopDisplay {
    constructor(scene, ui) {
        this.scene = scene;
        this.ui = ui;
    }
    
    /**
     * 显示主菜单
     * @param {Array} menuOptions - 菜单选项
     * @param {number} selectedIndex - 选中的索引
     * @param {Function} onItemClick - 点击回调
     */
    showMenu(menuOptions, selectedIndex, onItemClick) {
        this.ui.clearLists();
        
        menuOptions.forEach((option, index) => {
            const y = index * 40;
            const isSelected = index === selectedIndex;
            
            const optionText = this.scene.add.text(
                0,
                y,
                option,
                {
                    fontSize: '18px',
                    fill: UIStyleHelper.getMenuItemFill(isSelected),
                    fontFamily: 'Arial',
                    fontWeight: isSelected ? 'bold' : 'normal'
                }
            );
            
            // 添加鼠标交互
            optionText.setInteractive({ useHandCursor: true });
            optionText.on('pointerover', () => {
                if (onItemClick) onItemClick(index, 'hover');
            });
            optionText.on('pointerdown', () => {
                if (onItemClick) onItemClick(index, 'click');
            });
            
            this.ui.itemList.add(optionText);
        });
    }
    
    /**
     * 显示商品列表
     * @param {Array} shopData - 商品数据
     * @param {number} selectedIndex - 选中的索引
     * @param {number} playerMoney - 玩家金钱
     * @param {Function} onItemClick - 点击回调
     * @param {Function} onItemHover - 悬停回调
     */
    showShopItems(shopData, selectedIndex, playerMoney, onItemClick, onItemHover) {
        this.ui.clearLists();
        
        // 显示商品列表，并在末尾添加"返回"选项
        const itemsWithReturn = [...shopData, { name: '返回', isReturn: true }];
        
        itemsWithReturn.forEach((item, index) => {
            const y = index * 30;
            const isSelected = index === selectedIndex;
            
            // 如果是返回选项
            if (item.isReturn) {
                this.createReturnOption(y, isSelected, index, onItemClick, onItemHover);
                return;
            }
            
            // 商品项
            const canAfford = playerMoney >= item.price;
            
            // 商品名称
            const itemName = this.scene.add.text(
                0,
                y,
                `${item.name} - ${item.price} G`,
                {
                    fontSize: '16px',
                    fill: isSelected ? UIStyleHelper.MENU_COLORS.SELECTED :
                           (canAfford ? UIStyleHelper.MENU_COLORS.NORMAL : UIStyleHelper.MENU_COLORS.INACTIVE),
                    fontFamily: 'Arial'
                }
            );
            
            this.addMouseInteraction(itemName, index, onItemClick, onItemHover);
            
            // 库存标记（如果有）
            if (item.stock !== undefined) {
                const stockText = this.scene.add.text(
                    200,
                    y,
                    `库存: ${item.stock}`,
                    {
                        fontSize: '14px',
                        fill: '#aaaaaa',
                        fontFamily: 'Arial'
                    }
                );
                this.ui.itemList.add(stockText);
            }
            
            this.ui.itemList.add(itemName);
        });
    }
    
    /**
     * 显示可出售物品列表
     * @param {Array} sellableItems - 可出售物品列表
     * @param {number} selectedIndex - 选中的索引
     * @param {Function} calculateSellPrice - 计算出售价格的函数
     * @param {Function} onItemClick - 点击回调
     * @param {Function} onItemHover - 悬停回调
     */
    showSellableItems(sellableItems, selectedIndex, calculateSellPrice, onItemClick, onItemHover) {
        this.ui.clearLists();
        
        if (sellableItems.length === 0) {
            const emptyText = this.scene.add.text(
                0,
                0,
                '没有可出售的物品',
                {
                    fontSize: '16px',
                    fill: '#888888',
                    fontFamily: 'Arial'
                }
            );
            this.ui.itemList.add(emptyText);
            
            // 即使没有可出售物品，也显示返回选项
            this.createReturnOption(30, selectedIndex === 0, 0, onItemClick, onItemHover);
            return;
        }
        
        // 在列表末尾添加"返回"选项
        const itemsWithReturn = [...sellableItems, { item: { name: '返回' }, isReturn: true }];
        
        itemsWithReturn.forEach((itemData, index) => {
            const y = index * 30;
            const isSelected = index === selectedIndex;
            
            // 如果是返回选项
            if (itemData.isReturn) {
                this.createReturnOption(y, isSelected, index, onItemClick, onItemHover);
                return;
            }
            
            // 物品项
            const sellPrice = calculateSellPrice(itemData.item);
            
            // 物品名称和出售价格
            const itemName = this.scene.add.text(
                0,
                y,
                `${itemData.item.name} x${itemData.count} - ${sellPrice} G`,
                {
                    fontSize: '16px',
                    fill: UIStyleHelper.getMenuItemFill(isSelected),
                    fontFamily: 'Arial'
                }
            );
            
            this.addMouseInteraction(itemName, index, onItemClick, onItemHover);
            
            this.ui.itemList.add(itemName);
        });
    }
    
    /**
     * 显示确认选项
     * @param {number} selectedIndex - 选中的索引
     * @param {Function} onOptionClick - 点击回调
     */
    showConfirmOptions(selectedIndex, onOptionClick) {
        this.ui.clearLists();
        
        const options = ['确定', '取消'];
        options.forEach((option, index) => {
            const y = index * 40;
            const isSelected = index === selectedIndex;
            
            const optionText = this.scene.add.text(
                0,
                y,
                option,
                {
                    fontSize: '18px',
                    fill: UIStyleHelper.getMenuItemFill(isSelected),
                    fontFamily: 'Arial',
                    fontWeight: isSelected ? 'bold' : 'normal'
                }
            );
            
            // 添加鼠标交互
            optionText.setInteractive({ useHandCursor: true });
            optionText.on('pointerover', () => {
                if (onOptionClick) onOptionClick(index, 'hover');
            });
            optionText.on('pointerdown', () => {
                if (onOptionClick) onOptionClick(index, 'click');
            });
            
            this.ui.itemList.add(optionText);
        });
    }
    
    /**
     * 显示商品详情
     * @param {Object} item - 商品对象
     * @param {number} playerMoney - 玩家金钱
     */
    showItemDetails(item, playerMoney) {
        this.ui.itemDetails.removeAll(true);
        
        let y = 0;
        const lineHeight = 20;
        
        // 商品名称
        this.addDetailText(`名称: ${item.name}`, y, '#ffcc00');
        y += lineHeight;
        
        // 商品类型
        const typeText = this.getItemTypeText(item.type);
        this.addDetailText(`类型: ${typeText}`, y, '#ffffff');
        y += lineHeight;
        
        // 价格
        this.addDetailText(`价格: ${item.price} G`, y, 
            playerMoney >= item.price ? '#00ff00' : '#ff0000');
        y += lineHeight;
        
        y = this.addItemAttributes(item, y, lineHeight);
        this.addItemDescription(item, y);
    }
    
    /**
     * 显示出售物品详情
     * @param {Object} item - 物品对象
     * @param {number} count - 数量
     * @param {Function} calculateSellPrice - 计算出售价格的函数
     */
    showSellItemDetails(item, count, calculateSellPrice) {
        this.ui.itemDetails.removeAll(true);
        
        let y = 0;
        const lineHeight = 20;
        const sellPrice = calculateSellPrice(item);
        const totalPrice = sellPrice * count;
        
        // 物品名称
        this.addDetailText(`名称: ${item.name}`, y, '#ffcc00');
        y += lineHeight;
        
        // 数量
        this.addDetailText(`数量: ${count}`, y, '#ffffff');
        y += lineHeight;
        
        // 商品类型
        const typeText = this.getItemTypeText(item.type);
        this.addDetailText(`类型: ${typeText}`, y, '#ffffff');
        y += lineHeight;
        
        // 单价
        this.addDetailText(`单价: ${sellPrice} G`, y, '#ffff00');
        y += lineHeight;
        
        // 总价
        this.addDetailText(`总价: ${totalPrice} G`, y, '#00ff00');
        y += lineHeight;
        
        y = this.addItemAttributes(item, y, lineHeight);
        this.addItemDescription(item, y);
    }
    
    /**
     * 添加详情文本
     * @param {string} text - 文本内容
     * @param {number} y - Y坐标
     * @param {string} color - 文本颜色
     */
    addDetailText(text, y, color) {
        const textObj = this.scene.add.text(
            0,
            y,
            text,
            {
                fontSize: '14px',
                fill: color,
                fontFamily: 'Arial'
            }
        );
        this.ui.itemDetails.add(textObj);
    }
    
    /**
     * 获取物品类型文本
     * @param {string} type - 物品类型
     * @returns {string} 类型文本
     */
    getItemTypeText(type) {
        const typeMap = {
            'equipment': '装备',
            'consumable': '消耗品',
            'key': '关键道具',
            'quest_item': '任务物品',
            'weapon': '武器',
            'armor': '防具'
        };
        return typeMap[type] || type;
    }
    
    /**
     * 创建返回选项
     * @param {number} y - Y坐标
     * @param {boolean} isSelected - 是否选中
     * @param {number} index - 索引
     * @param {Function} onItemClick - 点击回调
     * @param {Function} onItemHover - 悬停回调
     */
    createReturnOption(y, isSelected, index, onItemClick, onItemHover) {
        const returnText = this.scene.add.text(
            0,
            y,
            '返回',
            {
                fontSize: '16px',
                fill: UIStyleHelper.getMenuItemFill(isSelected),
                fontFamily: 'Arial'
            }
        );
        
        this.addMouseInteraction(returnText, index, onItemClick, onItemHover);
        this.ui.itemList.add(returnText);
    }
    
    /**
     * 添加鼠标交互
     * @param {Phaser.GameObjects.Text} textObj - 文本对象
     * @param {number} index - 索引
     * @param {Function} onItemClick - 点击回调
     * @param {Function} onItemHover - 悬停回调
     */
    addMouseInteraction(textObj, index, onItemClick, onItemHover) {
        textObj.setInteractive({ useHandCursor: true });
        if (onItemHover) {
            textObj.on('pointerover', () => onItemHover(index));
        }
        if (onItemClick) {
            textObj.on('pointerdown', () => onItemClick(index));
        }
    }
    
    /**
     * 添加物品属性
     * @param {Object} item - 物品对象
     * @param {number} y - 当前Y坐标
     * @param {number} lineHeight - 行高
     * @returns {number} 新的Y坐标
     */
    addItemAttributes(item, y, lineHeight) {
        if (item.damage) {
            this.addDetailText(`攻击力: +${item.damage}`, y, '#ff5555');
            y += lineHeight;
        }
        if (item.defense) {
            this.addDetailText(`防御力: +${item.defense}`, y, '#5555ff');
            y += lineHeight;
        }
        if (item.healAmount) {
            this.addDetailText(`治疗效果: ${item.healAmount}`, y, '#55ff55');
            y += lineHeight;
        }
        if (item.range) {
            this.addDetailText(`攻击范围: ${item.range}`, y, '#ffff55');
            y += lineHeight;
        }
        return y;
    }
    
    /**
     * 添加物品描述
     * @param {Object} item - 物品对象
     * @param {number} y - Y坐标
     */
    addItemDescription(item, y) {
        if (item.description) {
            const descText = this.scene.add.text(
                0,
                y,
                `描述: ${item.description}`,
                {
                    fontSize: '14px',
                    fill: '#aaaaaa',
                    fontFamily: 'Arial',
                    wordWrap: { width: 250 }
                }
            );
            this.ui.itemDetails.add(descText);
        }
    }
}

